import React, { useState, useEffect, useCallback, useRef } from 'react';
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminPage from './pages/SuperAdminPage';
import HomePage from './pages/HomePage';
import Header from './components/Header';
import QuickButtons from './components/QuickButtons';
import ChatWindow from './components/ChatWindow';
import ContactForm from './components/ContactForm';
import LoadingSpinner from './components/LoadingSpinner';
import { guidebookAPI, chatAPI } from './services/api';
import { getUrlParameter, getSessionStorageKey, saveToSessionStorage, loadFromSessionStorage } from './utils/helpers';
import { FaPaperPlane, FaRedo } from 'react-icons/fa';
import './App.css';

/* ─────────────────────────────────────────────────────
   Route detection
   • ?guidebook= or ?id=  → public chatbot
   • ?admin (any value)   → admin login / dashboard
   • no params            → homepage
───────────────────────────────────────────────────── */
function getRoute() {
  const params = new URLSearchParams(window.location.search);
  if (params.get('guidebook') || params.get('id')) return 'chatbot';
  if (params.has('admin')) return 'admin';
  return 'home';
}

/* ─────────────────────────────────────────────────────
   Root app
───────────────────────────────────────────────────── */
export default function App() {
  const route = getRoute();
  if (route === 'chatbot') return <ChatbotApp />;
  if (route === 'admin')   return <AdminApp />;
  return <HomePage />;
}

/* ─────────────────────────────────────────────────────
   Force password reset screen
───────────────────────────────────────────────────── */
function ForcePasswordReset({ user, onDone }) {
  const API_BASE = process.env.REACT_APP_API_BASE;
  const [pw,       setPw]       = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [showPw,   setShowPw]   = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');

  const getToken = () =>
    localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

  const validate = () => {
    if (pw.length < 8)                          return 'At least 8 characters required.';
    if (!/[A-Z]/.test(pw))                      return 'Must contain an uppercase letter.';
    if (!/[a-z]/.test(pw))                      return 'Must contain a lowercase letter.';
    if (!/\d/.test(pw))                         return 'Must contain a number.';
    if (pw !== confirm)                         return 'Passwords do not match.';
    return '';
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ password: pw }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || 'Failed to update password.'); return; }
      onDone();
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pw, confirm]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg,#1A2744 0%,#2a3d6b 100%)', padding: 24,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, padding: '40px 36px', width: '100%', maxWidth: 420,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{ marginBottom: 24, textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%', background: 'rgba(200,16,46,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
          }}>
            <svg viewBox="0 0 24 24" fill="none" width="26" height="26">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke="#C8102E" strokeWidth="1.8"/>
              <path d="M7 11V7a5 5 0 0110 0v4" stroke="#C8102E" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1A2744' }}>Set New Password</h2>
          <p style={{ margin: '8px 0 0', fontSize: 13.5, color: '#6a7d9a' }}>
            Welcome, <strong>{user.name}</strong>! You must set a permanent password before continuing.
          </p>
        </div>

        {error && (
          <div style={{
            background: 'rgba(200,16,46,0.06)', border: '1px solid rgba(200,16,46,0.2)',
            borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#C8102E', marginBottom: 16,
          }}>{error}</div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {[
            { label: 'New Password', val: pw, set: setPw },
            { label: 'Confirm Password', val: confirm, set: setConfirm },
          ].map(({ label, val, set }) => (
            <div key={label} style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11.5, fontWeight: 700, color: '#6a7d9a', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 5 }}>
                {label}
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type={showPw ? 'text' : 'password'}
                  value={val}
                  onChange={e => set(e.target.value)}
                  placeholder="••••••••••"
                  autoComplete="new-password"
                  style={{
                    width: '100%', padding: '9px 38px 9px 12px', border: '1.5px solid rgba(26,39,68,0.15)',
                    borderRadius: 9, fontSize: 13.5, color: '#1A2744', background: '#fff', outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(v => !v)}
                  style={{ position: 'absolute', right: 10, background: 'none', border: 'none', cursor: 'pointer', color: '#9aaabf', padding: 2 }}
                >
                  {showPw
                    ? <svg viewBox="0 0 20 20" fill="none" width="16" height="16"><path d="M3 3l14 14M12.45 12.45A3 3 0 017.55 7.55M8.7 5.1A8.7 8.7 0 0110 5c5 0 8 5 8 5a15.3 15.3 0 01-2.7 3.3M6.5 6.5A14.5 14.5 0 002 10s3 5 8 5a8.6 8.6 0 004.5-1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                    : <svg viewBox="0 0 20 20" fill="none" width="16" height="16"><path d="M2 10s3-6 8-6 8 6 8 6-3 6-8 6-8-6-8-6z" stroke="currentColor" strokeWidth="1.5"/><circle cx="10" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
                  }
                </button>
              </div>
            </div>
          ))}

          <p style={{ fontSize: 11.5, color: '#9aaabf', margin: '0 0 20px' }}>
            8+ chars · uppercase · lowercase · number
          </p>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '11px 0', background: '#C8102E', color: '#fff',
              border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            {loading
              ? <span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />
              : 'Set Password & Continue'
            }
          </button>
        </form>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────
   Admin area (login → force-reset? → dashboard)
───────────────────────────────────────────────────── */
function AdminApp() {
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Re-hydrate session from storage
    const token =
      localStorage.getItem('auth_token') ||
      sessionStorage.getItem('auth_token');
    const raw =
      localStorage.getItem('user') ||
      sessionStorage.getItem('user');
    if (token && raw) {
      try { setUser(JSON.parse(raw)); } catch { /* stale data */ }
    }
    setChecked(true);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user');
    setUser(null);
  };

  const handleLoginSuccess = (u) => {
    setUser(u);
  };

  const handlePasswordReset = useCallback(() => {
    const updated = { ...user, must_reset_password: false };
    const storage = localStorage.getItem('auth_token') ? localStorage : sessionStorage;
    storage.setItem('user', JSON.stringify(updated));
    setUser(updated);
  }, [user]);

  if (!checked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <LoadingSpinner message="Loading…" />
      </div>
    );
  }

  if (!user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  if (user.must_reset_password) {
    return <ForcePasswordReset user={user} onDone={handlePasswordReset} />;
  }

  if (user.is_super_admin) {
    return <SuperAdminPage user={user} onLogout={handleLogout} />;
  }

  return <AdminDashboard user={user} onLogout={handleLogout} />;
}

/* ─────────────────────────────────────────────────────
   Public chatbot (unchanged)
───────────────────────────────────────────────────── */
function ChatbotApp() {
  const [guidebook, setGuidebook] = useState(null);
  const [cards, setCards] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [awaitingContact, setAwaitingContact] = useState(false);
  const [pendingQuestion, setPendingQuestion] = useState('');
  const [error, setError] = useState(null);
  // Capture timezone immediately (synchronous) so location is never null on first message.
  // ipapi.co replaces it with city/country once the async call resolves.
  const locationRef = useRef((() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return null; }
  })());

  const guidebookSlug = getUrlParameter('guidebook');
  const API_BASE = process.env.REACT_APP_API_BASE;

  // Register service worker once on mount
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  useEffect(() => {
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then(d => {
        if (d && d.city && d.country_name) locationRef.current = `${d.city}, ${d.country_name}`;
        else if (d && d.country_name)       locationRef.current = d.country_name;
      })
      .catch(() => { /* keep timezone fallback already in ref */ });
  }, []);

  useEffect(() => {
    if (guidebookSlug) {
      loadGuidebook();
      loadSessionData();
    } else {
      setError('No guidebook specified in URL');
      setInitialLoading(false);
    }
    // eslint-disable-next-line
  }, [guidebookSlug]);

  const loadGuidebook = async () => {
    try {
      const [data, cardsData] = await Promise.all([
        guidebookAPI.getBySlug(guidebookSlug),
        guidebookAPI.getCards(guidebookSlug),
      ]);
      setGuidebook(data);
      setCards(cardsData);
    } catch (err) {
      setError(err.message || 'Failed to load guidebook');
    } finally {
      setInitialLoading(false);
    }
  };

  const loadSessionData = () => {
    const storageKey = getSessionStorageKey(guidebookSlug);
    const savedData = loadFromSessionStorage(storageKey);
    if (savedData) {
      setSessionId(savedData.sessionId);
      setMessages(savedData.messages || []);
    }
  };

  const saveSessionData = (sid, msgs) => {
    const storageKey = getSessionStorageKey(guidebookSlug);
    saveToSessionStorage(storageKey, { sessionId: sid, messages: msgs });
  };

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const userMessage = { role: 'user', content: text, timestamp: new Date().toISOString() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput('');
    setLoading(true);
    setStreamingContent('');
    let accumulated = '';

    try {
      await chatAPI.sendMessageStream(
        guidebookSlug, text, sessionId,
        (chunk) => { accumulated += chunk; setStreamingContent(accumulated); },
        (doneData) => {
          const newSessionId = doneData.session_id;
          if (!sessionId) setSessionId(newSessionId);
          const aiMessage = { role: 'assistant', content: accumulated, timestamp: new Date().toISOString() };
          const updatedMessages = [...newMessages, aiMessage];
          setMessages(updatedMessages);
          saveSessionData(newSessionId || sessionId, updatedMessages);
          setStreamingContent('');
          setLoading(false);
          if (doneData.needs_contact) {
            setAwaitingContact(true);
            setPendingQuestion(text);
            subscribeToPush(newSessionId || sessionId);
          }
        },
        (errorMsg) => {
          setMessages([...newMessages, { role: 'assistant', content: errorMsg || 'Sorry, an error occurred.', timestamp: new Date().toISOString() }]);
          setStreamingContent('');
          setLoading(false);
        },
        locationRef.current,
      );
    } catch (err) {
      setMessages([...newMessages, { role: 'assistant', content: err.message || 'Sorry, an error occurred.', timestamp: new Date().toISOString() }]);
      setStreamingContent('');
      setLoading(false);
    }
  };

  function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    return Uint8Array.from([...raw].map(c => c.charCodeAt(0)));
  }

  async function subscribeToPush(sid) {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await fetch(`${API_BASE}/api/chat/vapid-public-key`);
      if (!keyRes.ok) return;
      const { public_key } = await keyRes.json();
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(public_key),
      });
      const sub = subscription.toJSON();
      await fetch(`${API_BASE}/api/chat/push-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sid,
          endpoint: sub.endpoint,
          p256dh: sub.keys.p256dh,
          auth: sub.keys.auth,
        }),
      });
    } catch {
      // Push is optional — silent fail
    }
  }

  const handleContactSubmit = async (phone, email) => {
    try {
      await chatAPI.submitContact(sessionId, pendingQuestion, phone, email);
      const parts = [];
      if (phone) parts.push(`phone (${phone})`);
      if (email) parts.push(`email (${email})`);
      const confirmMessage = {
        role: 'assistant',
        content: `✅ Thank you! We've saved your contact. The property manager will reach out via ${parts.join(' or ')} soon.`,
        timestamp: new Date().toISOString(),
      };
      const updatedMessages = [...messages, confirmMessage];
      setMessages(updatedMessages);
      saveSessionData(sessionId, updatedMessages);
      setAwaitingContact(false);
      setPendingQuestion('');
    } catch (err) {
      alert(err.message || 'Failed to save contact information');
    }
  };

  const handleContactSkip = () => {
    const skip = { role: 'assistant', content: 'No problem! Feel free to ask another question.', timestamp: new Date().toISOString() };
    const updatedMessages = [...messages, skip];
    setMessages(updatedMessages);
    saveSessionData(sessionId, updatedMessages);
    setAwaitingContact(false);
    setPendingQuestion('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const handleNewChat = () => {
    if (window.confirm('Start a new conversation? Current history will be cleared.')) {
      setMessages([]);
      setSessionId(null);
      setAwaitingContact(false);
      setPendingQuestion('');
      sessionStorage.removeItem(getSessionStorageKey(guidebookSlug));
    }
  };

  if (initialLoading) return <div className="app-container"><LoadingSpinner message="Loading chatbot..." /></div>;

  if (error) return (
    <div className="app-container">
      <div className="error-container">
        <div className="error-icon">❌</div>
        <h1>Oops! Something went wrong</h1>
        <p>{error}</p>
        <button className="error-retry-btn" onClick={() => window.location.reload()}>🔄 Retry</button>
      </div>
    </div>
  );

  if (!guidebook) return (
    <div className="app-container">
      <div className="error-container">
        <div className="error-icon">🔍</div>
        <h1>Guidebook Not Found</h1>
        <p>The requested guidebook could not be found.</p>
      </div>
    </div>
  );

  return (
    <div className="app-container">
      <Header title={guidebook.guidebook_title} description={guidebook.chatbot_description || 'Ask me anything about this property!'} />
      {messages.length > 0 && (
        <div className="chat-controls">
          <button className="new-chat-btn" onClick={handleNewChat}><FaRedo /> New Chat</button>
        </div>
      )}
      {messages.length === 0 && !awaitingContact && <QuickButtons cards={cards} onButtonClick={sendMessage} />}
      <ChatWindow messages={messages} loading={loading} streamingMessage={streamingContent} />
      {awaitingContact && <ContactForm onSubmit={handleContactSubmit} onSkip={handleContactSkip} />}
      <div className="input-container">
        <input
          type="text"
          className="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Ask me anything related to this property..."
          disabled={awaitingContact || loading}
        />
        <button className="send-button" onClick={() => sendMessage(input)} disabled={awaitingContact || loading || !input.trim()}>
          <FaPaperPlane />
        </button>
      </div>
      <div className="app-footer"><p>Powered by AI • Secure & Private</p></div>
    </div>
  );
}
